import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  MailSearch, Loader2, Play, Search, CheckCircle2, XCircle, AlertTriangle,
  MailX, Star, ChevronLeft, ChevronRight, Lock, Building2, Zap,
} from "lucide-react";

const STATUS_META = {
  active: { label: "Active", cls: "bg-emerald-50 text-emerald-600 border-emerald-200" },
  bounced: { label: "Bounced", cls: "bg-rose-50 text-rose-600 border-rose-200" },
  invalid_syntax: { label: "Invalid", cls: "bg-rose-50 text-rose-600 border-rose-200" },
  no_mx: { label: "No Mail Server", cls: "bg-orange-50 text-orange-600 border-orange-200" },
  missing_email: { label: "Missing Email", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

function StatusChip({ status }) {
  const m = STATUS_META[status] || STATUS_META.missing_email;
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-lg border text-xs font-semibold ${m.cls}`}>{m.label}</span>;
}

function CompanyModal({ company, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [customEmail, setCustomEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/contact-audit/company-contacts", { params: { company } })
      .then((r) => setData(r.data)).catch(() => toast.error("Could not load contacts."));
  }, [company]);

  useEffect(() => { load(); }, [load]);

  const pin = async (email, name) => {
    setBusy(true);
    try {
      await api.post("/admin/contact-audit/pin", { company, email, name: name || "" });
      toast.success(`Primary contact set to ${email}`);
      load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not pin contact."); }
    finally { setBusy(false); }
  };

  const unpin = async () => {
    setBusy(true);
    try {
      await api.delete("/admin/contact-audit/pin", { params: { company } });
      toast.success("Override removed — smart auto-pick restored.");
      load(); onChanged();
    } catch { toast.error("Could not remove override."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="company-contacts-modal">
      <div className="bg-white rounded-2xl w-full max-w-xl shadow-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="font-bold text-slate-900">{company}</h2>
            <p className="text-xs text-slate-500 mt-0.5">Pick which contact receives applications</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none px-2">×</button>
        </div>
        <div className="p-6 overflow-y-auto space-y-3">
          {!data ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-amber-500" /></div>
          ) : (
            <>
              {data.resolved && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm">
                  <span className="font-semibold text-amber-800">Currently resolves to: </span>
                  <span className="text-amber-700">{data.resolved.email}</span>
                  <span className="text-amber-600 text-xs ml-2">({data.resolved.source === "pinned" ? "Pinned by admin" : "Auto-picked by ranking"})</span>
                </div>
              )}
              {data.items.length === 0 && <p className="text-sm text-slate-500 text-center py-4">No synced contacts for this company.</p>}
              {data.items.filter((c) => c.email).map((c) => (
                <div key={c.contact_id} className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${c.pinned ? "border-amber-400 bg-amber-50/50" : "border-slate-200"}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-slate-900">{c.name || "—"}</p>
                      {c.pinned && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500 text-black text-[10px] font-bold"><Star className="w-3 h-3" /> Primary</span>}
                    </div>
                    <p className="text-xs text-slate-500">{c.title || "No title"} · {c.email}</p>
                  </div>
                  <StatusChip status={c.status} />
                  {!c.pinned && (
                    <button data-testid={`pin-${c.contact_id}`} onClick={() => pin(c.email, c.name)} disabled={busy}
                      className="text-xs font-semibold text-amber-700 hover:underline shrink-0">Set Primary</button>
                  )}
                </div>
              ))}
              <div className="pt-2 border-t border-slate-100">
                <p className="text-xs font-semibold text-slate-600 mb-2">Pin a custom email (not in directory):</p>
                <div className="flex gap-2">
                  <Input data-testid="custom-pin-input" value={customEmail} onChange={(e) => setCustomEmail(e.target.value)}
                    placeholder="hr@company.com" className="h-10 bg-white border-slate-300 text-sm" />
                  <Button data-testid="custom-pin-button" onClick={() => pin(customEmail, "HR Team")} disabled={busy || !customEmail.includes("@")}
                    className="h-10 bg-amber-500 text-black hover:bg-amber-400 font-semibold shrink-0">Pin</Button>
                </div>
              </div>
              {data.override && (
                <button onClick={unpin} disabled={busy} data-testid="unpin-button"
                  className="text-xs text-rose-600 hover:underline">Remove override (back to auto-pick)</button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ContactAudit() {
  const [access, setAccess] = useState(null);
  const [summary, setSummary] = useState(null);
  const [data, setData] = useState({ items: [], total: 0 });
  const [filters, setFilters] = useState({ status: "", search: "" });
  const [page, setPage] = useState(1);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalCompany, setModalCompany] = useState(null);
  const pageSize = 15;

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data: acc } = await api.get("/admin/access");
      setAccess(acc.is_admin);
      if (!acc.is_admin) return;
      const [{ data: sum }, { data: list }] = await Promise.all([
        api.get("/admin/contact-audit/summary"),
        api.get("/admin/contact-audit/contacts", { params: { ...filters, page, page_size: pageSize } }),
      ]);
      setSummary(sum);
      setData(list);
    } catch { setAccess(false); }
    finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const runAudit = async () => {
    setRunning(true);
    try {
      const { data: res } = await api.post("/admin/contact-audit/run", null, { timeout: 120000 });
      toast.success(`Audit complete — ${res.total_contacts} contacts scanned, ${res.active} active.`);
      await load(true);
    } catch { toast.error("Audit failed."); }
    finally { setRunning(false); }
  };

  if (loading && !summary) return <div className="py-24 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-amber-500" /></div>;
  if (!access) return (
    <div className="py-24 text-center" data-testid="contact-audit-denied">
      <Lock className="w-10 h-10 mx-auto mb-3 text-slate-300" />
      <p className="font-semibold text-slate-800">Admins only</p>
    </div>
  );

  const CARDS = summary?.ran ? [
    { label: "Total Contacts", value: summary.total_contacts, Icon: MailSearch, cls: "bg-sky-50 text-sky-500" },
    { label: "Active & Verified", value: summary.active, Icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-500" },
    { label: "Needs Review", value: (summary.bounced || 0) + (summary.invalid_syntax || 0) + (summary.no_mx || 0), Icon: AlertTriangle, cls: "bg-orange-50 text-orange-500" },
    { label: "Missing Email", value: summary.missing_email, Icon: MailX, cls: "bg-slate-100 text-slate-500" },
    { label: "Companies w/o Contact", value: summary.companies_without_contact, Icon: Building2, cls: "bg-rose-50 text-rose-500" },
  ] : [];

  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

  return (
    <div data-testid="contact-audit-page" className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-display">Contact Audit &amp; Health</h1>
          <p className="text-slate-500 mt-1">Verify company contact emails (syntax, mail server, bounce history) and control who receives applications.</p>
        </div>
        <div className="flex items-center gap-3">
          {summary?.ran && <span className="text-xs text-slate-400" data-testid="audit-last-run">Last run: {new Date(summary.last_run).toLocaleString()}</span>}
          <Button data-testid="run-audit-button" onClick={runAudit} disabled={running}
            className="bg-amber-500 text-black hover:bg-amber-400 font-semibold">
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            {running ? "Auditing…" : "Run Audit"}
          </Button>
        </div>
      </div>

      {!summary?.ran ? (
        <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center text-slate-500" data-testid="audit-empty">
          <Zap className="w-10 h-10 mx-auto mb-3 text-amber-400" />
          <p className="font-semibold text-slate-800">No audit has run yet</p>
          <p className="text-sm mt-1">Click "Run Audit" to scan every synced company contact for deliverability.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {CARDS.map((c) => (
              <div key={c.label} data-testid={`audit-card-${c.label.replace(/\W+/g, "-").toLowerCase()}`}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl ${c.cls} flex items-center justify-center shrink-0`}><c.Icon className="w-5 h-5" /></div>
                <div><p className="text-[11px] text-slate-500 leading-tight">{c.label}</p><p className="text-xl font-bold text-slate-900">{c.value}</p></div>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-slate-100">
              <h2 className="font-bold text-slate-900 mr-auto">Contacts ({data.total})</h2>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input data-testid="audit-search-input" value={filters.search}
                  onChange={(e) => { setFilters({ ...filters, search: e.target.value }); setPage(1); }}
                  placeholder="Search company, contact or email…"
                  className="h-10 w-60 pl-9 pr-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-amber-500" />
              </div>
              <select data-testid="audit-status-filter" value={filters.status}
                onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }}
                className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-600 outline-none focus:border-amber-500">
                <option value="">All health statuses</option>
                <option value="active">Active</option>
                <option value="bounced">Bounced</option>
                <option value="no_mx">No Mail Server</option>
                <option value="invalid_syntax">Invalid Syntax</option>
                <option value="missing_email">Missing Email</option>
              </select>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px]">
                <thead>
                  <tr className="text-left text-xs font-semibold text-slate-500 border-b border-slate-100">
                    <th className="px-5 py-3">Company</th>
                    <th className="px-3 py-3">Contact</th>
                    <th className="px-3 py-3">Email</th>
                    <th className="px-3 py-3">Health</th>
                    <th className="px-3 py-3">Mail Server</th>
                    <th className="px-3 py-3 pr-5 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((c) => (
                    <tr key={c.contact_id} data-testid={`audit-row-${c.contact_id}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                      <td className="px-5 py-3 text-sm font-semibold text-slate-900 max-w-[220px] truncate">
                        {c.company || "—"}
                        {c.company_has_override && <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold align-middle"><Star className="w-3 h-3" /> Pinned</span>}
                      </td>
                      <td className="px-3 py-3 text-sm text-slate-600">
                        <p className="font-medium text-slate-800">{c.name || "—"}</p>
                        <p className="text-xs text-slate-400">{c.title || ""}</p>
                      </td>
                      <td className="px-3 py-3 text-sm text-slate-600">{c.email || "—"}</td>
                      <td className="px-3 py-3"><StatusChip status={c.status} /></td>
                      <td className="px-3 py-3 text-xs text-slate-400 font-mono">{c.mx_host || "—"}</td>
                      <td className="px-3 py-3 pr-5 text-right">
                        <button data-testid={`manage-${c.contact_id}`} onClick={() => setModalCompany(c.company)}
                          className="text-xs font-semibold text-amber-700 hover:underline">Manage</button>
                      </td>
                    </tr>
                  ))}
                  {data.items.length === 0 && (
                    <tr><td colSpan={6} className="py-12 text-center text-slate-500">No contacts match.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between px-5 py-3.5 border-t border-slate-100">
              <p className="text-sm text-slate-500">Page {page} of {totalPages}</p>
              <div className="flex items-center gap-1.5">
                <button data-testid="audit-prev" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                  className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
                <button data-testid="audit-next" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        </>
      )}

      {modalCompany && (
        <CompanyModal company={modalCompany} onClose={() => setModalCompany(null)} onChanged={() => load(true)} />
      )}
    </div>
  );
}
